import classNames from 'classnames'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import esLocale from 'date-fns/locale/es'

import styles from './AppDatePicker.module.scss'
import {
  getNowDateTimeLocalInputValue,
  getTodayDateInputValue,
  toDateInputValue,
  toDateTimeLocalInputValue,
  fromDateInputValue,
  fromDateTimeLocalInputValue
} from './datePicker.helpers'

const getInputValue = ({ type, value, valueType }) => {
  if (type === 'datetime-local') return toDateTimeLocalInputValue(value)
  if (valueType === 'date') return toDateInputValue(value)

  return value || ''
}

const getChangeValue = ({ type, value, valueType }) => {
  if (valueType !== 'date') return value

  return type === 'datetime-local' ? fromDateTimeLocalInputValue(value) : fromDateInputValue(value)
}

const getMaxValue = ({ disableFuture, max, type }) => {
  if (max) return max
  if (!disableFuture) return undefined

  return type === 'datetime-local' ? getNowDateTimeLocalInputValue() : getTodayDateInputValue()
}

const buildChangeEvent = ({ event, name, value }) => ({
  ...event,
  target: {
    ...event?.target,
    name,
    value
  }
})

const AppDatePicker = ({
  className,
  disabled = false,
  disableFuture = false,
  error = false,
  helperText,
  inline = false,
  inputClassName,
  label,
  max,
  min,
  name,
  onChange,
  pickerVariant = 'mui',
  readOnly = false,
  required = false,
  type = 'date',
  value,
  valueType = 'string'
}) => {
  const maxValue = getMaxValue({ disableFuture, max, type })
  const isDatePicker = type === 'date'
  const inputValue = getInputValue({ type, value, valueType })
  const dateValue = isDatePicker ? fromDateInputValue(inputValue) : null
  const minDate = min ? fromDateInputValue(min) : undefined
  const maxDate = maxValue ? fromDateInputValue(maxValue) : undefined

  const handleChange = event => {
    if (onChange) onChange(getChangeValue({ type, value: event.target.value, valueType }), event)
  }

  const handleDateChange = nextDate => {
    const nextDateValue = toDateInputValue(nextDate)

    if (onChange) {
      onChange(
        getChangeValue({ type, value: nextDateValue, valueType }),
        buildChangeEvent({ name, value: nextDateValue })
      )
    }
  }

  const muiDateControl = (
    <LocalizationProvider
      adapterLocale={esLocale}
      dateAdapter={AdapterDateFns}
    >
      <DesktopDatePicker
        className={classNames(styles.inputGroup, inputClassName, {
          [styles.disabled]: disabled,
          [styles.hasError]: error
        })}
        disabled={disabled}
        disableFuture={disableFuture}
        format='dd/MM/yyyy'
        maxDate={maxDate}
        minDate={minDate}
        onChange={handleDateChange}
        readOnly={readOnly}
        slots={{
          openPickerIcon: CalendarMonthOutlinedIcon
        }}
        slotProps={{
          desktopPaper: {
            className: styles.pickerPaper
          },
          openPickerButton: {
            className: styles.openPickerButton
          },
          textField: {
            error,
            inputProps: {
              'aria-invalid': error || undefined,
              inputMode: 'numeric',
              required
            },
            name,
            required,
            variant: 'standard'
          }
        }}
        value={dateValue}
      />
    </LocalizationProvider>
  )

  const nativeDateControl = (
    <input
      aria-invalid={error || undefined}
      className={classNames(styles.input, { [styles.hasError]: error }, inputClassName)}
      disabled={disabled}
      max={maxValue}
      min={min}
      name={name}
      onChange={handleChange}
      readOnly={readOnly}
      required={required}
      type='date'
      value={inputValue}
    />
  )

  const dateControl = pickerVariant === 'native' ? nativeDateControl : muiDateControl

  const nativeControl = (
    <input
      aria-invalid={error || undefined}
      className={classNames(styles.input, { [styles.hasError]: error }, inputClassName)}
      disabled={disabled}
      max={maxValue}
      min={min}
      name={name}
      onChange={handleChange}
      readOnly={readOnly}
      required={required}
      type={type}
      value={inputValue}
    />
  )

  if (inline && !label && !helperText) {
    return isDatePicker ? dateControl : nativeControl
  }

  return (
    <label className={classNames(styles.field, className)}>
      {label && <span className={styles.label}>{label}</span>}
      {isDatePicker ? dateControl : nativeControl}
      {helperText && <span className={styles.helperText}>{helperText}</span>}
    </label>
  )
}

export const AppDateTimePicker = props => (
  <AppDatePicker
    {...props}
    type='datetime-local'
  />
)

export default AppDatePicker
